import { Check, Close } from "@mui/icons-material";
import { IconButton, Input } from "@mui/joy";
import { KeyboardEvent, useEffect, useRef, useState } from "react";

type InlineNameFieldProps = {
  // Read out by screen readers, since the field has no visible label.
  label: string;
  initialValue?: string;
  // Enter saves and clears the field for the next name, instead of closing it.
  keepOpen?: boolean;
  width?: number;
  // Should reject if the save fails, so the field stays open with the text.
  onSave: (name: string) => Promise<unknown>;
  onClose: () => void;
};

export default function InlineNameField({
  label,
  initialValue = "",
  keepOpen = false,
  width = 170,
  onSave,
  onClose,
}: InlineNameFieldProps) {
  const [value, setValue] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const save = async () => {
    const name = value.trim();
    if (!name || isSaving) {
      return;
    }
    if (name === initialValue) {
      onClose();
      return;
    }
    setIsSaving(true);
    try {
      await onSave(name);
    } catch {
      // The toast has the reason. Leave the text so it can be fixed.
      return;
    } finally {
      setIsSaving(false);
    }
    if (keepOpen) {
      setValue("");
      inputRef.current?.focus();
    } else {
      onClose();
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      save();
    } else if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
    }
  };

  return (
    <Input
      size="sm"
      variant="outlined"
      placeholder="Option name"
      value={value}
      onChange={(event) => setValue(event.target.value)}
      onKeyDown={onKeyDown}
      slotProps={{
        input: { ref: inputRef, "aria-label": label, maxLength: 255 },
      }}
      sx={{ width, borderRadius: 999, "--Input-focusedThickness": "2px" }}
      endDecorator={
        <>
          <IconButton size="sm" variant="plain" color="primary" aria-label="Save" onClick={save}>
            <Check fontSize="small" />
          </IconButton>
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            aria-label="Cancel"
            onClick={onClose}
          >
            <Close fontSize="small" />
          </IconButton>
        </>
      }
    />
  );
}
